import { useEffect, useRef } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from '@/store/languageStore';

export interface TripSpeedPoint {
  index: number;
  timestamp: string;
  speed: number;
  speedLimit: number | null;
  overSpeed: number | null;
  tripOdometer: number | null;
}

interface ChartMouse {
  activeTooltipIndex?: number | string | null;
}

function indexFromChart(state: ChartMouse | null | undefined, length: number): number | null {
  if (state?.activeTooltipIndex == null || state.activeTooltipIndex === '') return null;
  const index = Number(state.activeTooltipIndex);
  if (!Number.isFinite(index)) return null;
  const next = Math.round(index);
  if (next < 0 || next >= length) return null;
  return next;
}

interface Props {
  data: TripSpeedPoint[];
  currentIndex: number;
  hasSpeedLimitData: boolean;
  onSeek: (index: number) => void;
}

export default function TripSpeedChart({
  data,
  currentIndex,
  hasSpeedLimitData,
  onSeek,
}: Props) {
  const { t } = useTranslation();
  const scrubbing = useRef(false);

  useEffect(() => {
    const stop = () => {
      scrubbing.current = false;
    };
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, []);

  const seekFrom = (state: ChartMouse | null | undefined) => {
    const index = indexFromChart(state, data.length);
    if (index != null) onSeek(index);
  };

  return (
    <div className="cursor-crosshair">
      <ResponsiveContainer width="100%" height={200}>
        <LineChart
          data={data}
          margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
          onMouseDown={(state) => {
            scrubbing.current = true;
            seekFrom(state);
          }}
          onMouseMove={(state) => {
            if (!scrubbing.current) return;
            seekFrom(state);
          }}
          onClick={(state) => seekFrom(state)}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="index"
            tick={{ fontSize: 10 }}
            angle={-45}
            textAnchor="end"
            height={60}
            interval="preserveStartEnd"
            tickFormatter={(value) => data[Number(value)]?.timestamp ?? ''}
          />
          <YAxis
            label={{ value: `${t('trips.speedChart')} (${t('trips.speedUnit')})`, angle: -90, position: 'insideLeft' }}
            domain={[0, 'dataMax + 10']}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
            }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const point = payload[0]?.payload as TripSpeedPoint | undefined;
              if (!point) return null;
              return (
                <div className="text-xs p-2">
                  <div>{t('trips.time')}: {point.timestamp}</div>
                  <div>{t('trips.speed')}: {point.speed} {t('trips.speedUnit')}</div>
                  {point.speedLimit != null && (
                    <div>{t('trips.speedLimit')}: {point.speedLimit} {t('trips.speedUnit')}</div>
                  )}
                  {point.tripOdometer != null && (
                    <div>
                      {t('trips.avl199')}: {(point.tripOdometer / 1000).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km
                    </div>
                  )}
                </div>
              );
            }}
          />
          <Legend />
          {data.length > 0 && currentIndex >= 0 && currentIndex < data.length && (
            <ReferenceLine x={currentIndex} stroke="#2563eb" strokeWidth={2} />
          )}
          <Line
            type="monotone"
            dataKey="speed"
            stroke="#10b981"
            strokeWidth={2}
            dot={false}
            name={t('trips.vehicleSpeed')}
            activeDot={{ r: 4 }}
          />
          {hasSpeedLimitData && (
            <Line
              type="monotone"
              dataKey="speedLimit"
              stroke="#ef4444"
              strokeWidth={2}
              dot={false}
              name={t('trips.speedLimit')}
              connectNulls={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
